import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { ConversationMessageKey } from 'flow/types/TicketEventData';
import { TicketKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectEventDTOSubtype,
    selectEventSender,
    selectEventTimestamp,
    selectMessageDeliveryStatusIsSending,
} from 'flow/selectors/TicketEventSelectors';
import MessageItemSent from 'flow/components/message/MessageItemSent';
import MessageSender from 'flow/components/tickets/MessageSender';
import SenderAvatar from 'flow/components/tickets/internal-note/SenderAvatar';
import AttachmentsContainer from 'flow/components/tickets/event-item/Messages/AttachmentsContainer';
import DeliveryStatus from 'flow/components/tickets/event-item/Messages/DeliveryStatus';
import MessageStyled from 'flow/components/tickets/event-item/Messages/MessageStyled';

interface Props {
    ticketKey: TicketKey;
    eventKey: ConversationMessageKey;
    onNewAttachmentsFetched?: () => void;
}

const MessageV2 = ({ ticketKey, eventKey, onNewAttachmentsFetched }: Props) => {
    const { i18next } = useAppContext();

    const isInternalNote = useAppSelector(
        (state) =>
            selectEventDTOSubtype(state, ticketKey, eventKey) ===
            'OmniInternalNoteTicketEvent',
    );
    const isSMS = useAppSelector(
        (state) =>
            selectEventDTOSubtype(state, ticketKey, eventKey) ===
            'OmniSmsNoteTicketEvent',
    );
    const sender = useAppSelector((state) =>
        selectEventSender(state, ticketKey, eventKey),
    );
    const timestamp = useAppSelector((state) =>
        selectEventTimestamp(state, ticketKey, eventKey),
    );
    const deliveryStatusIsSending = useAppSelector((state) =>
        selectMessageDeliveryStatusIsSending(state, ticketKey, eventKey),
    );

    return (
        <div
            className={twMerge(
                'flex flex-col gap-1 rounded-xl p-3',
                deliveryStatusIsSending && 'animate-fade opacity-50',
                // Internal notes keep the amber card; customer messages (SMS
                // here) get the same card in the call-card gray.
                isInternalNote
                    ? 'bg-[#FCF2D8] dark:bg-[#332E1A]'
                    : 'bg-gray-150',
            )}
        >
            <div className="flex h-6 items-center gap-2 text-sm">
                <SenderAvatar sender={sender} size="small" />
                <MessageSender ticketKey={ticketKey} messageKey={eventKey} />
                <span className="text-sm text-gray-500">
                    {isInternalNote
                        ? i18next.t('tickets.internal.note')
                        : isSMS
                          ? i18next.t('option.sms')
                          : null}
                </span>
                <span className="ms-auto text-sm text-gray-500">
                    <MessageItemSent timeString={timestamp} />
                </span>
            </div>
            {/* ps-8 aligns the body under the sender name (avatar size-6 +
                gap-2), inside the card's p-3 padding. */}
            <div className="relative ps-8 wrap-break-word whitespace-pre-wrap">
                <div className="min-w-0 text-base wrap-break-word text-gray-800 no-underline">
                    <MessageStyled
                        ticketKey={ticketKey}
                        messageKey={eventKey}
                    />
                    <AttachmentsContainer
                        ticketKey={ticketKey}
                        eventKey={eventKey}
                        onNewAttachmentsFetched={onNewAttachmentsFetched}
                        isBeingSent={deliveryStatusIsSending}
                    />
                </div>
                <div className="absolute end-1 bottom-1">
                    <DeliveryStatus
                        messageKey={eventKey}
                        ticketKey={ticketKey}
                    />
                </div>
            </div>
        </div>
    );
};

export default React.memo(MessageV2);
